// src/pages/TradeJournaling.jsx
import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../auth/AuthContext";
import CardWrapper from "../components/CardWrapper";
import GuestBanner from "../components/GuestBanner";
import { normalizeJournal } from "../lib/normalize";
import { toFloatLoose, toIntLoose } from "../lib/parse";

Modal.setAppElement("#root");

const TradeJournaling = () => {
  const { api, user } = useAuth();
  const isGuest = user?.type === "guest";
  const isAuthed = !!user && !isGuest;

  const [formData, setFormData] = useState({
    ticker: "",
    notes: "",
    entry_price: "",
    exit_price: "",
    position_size: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    id: null,
    ticker: "",
    notes: "",
    entry_price: "",
    exit_price: "",
    position_size: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [entries, setEntries] = useState([]);
  const [tempEntries, setTempEntries] = useState([]); // anon-only buffer (clears on unmount/refresh)
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const fetchEntries = async () => {
    try {
      if (!isAuthed && !isGuest) {
        setEntries([]);
        return;
      }
      const rows = await api("/api/journal");
      setEntries(Array.isArray(rows) ? rows.map(normalizeJournal) : []);
    } catch (err) {
      console.error("Error fetching journal entries:", err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthed && !isGuest) {
      localStorage.removeItem("journal_entries"); // defensive
    }
    setLoading(true);
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, isGuest]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Anonymous: save locally (in memory) so it appears, wipe on refresh/nav
    if (!isAuthed && !isGuest) {
      const local = {
        id: `temp-${Date.now()}`,
        ticker: formData.ticker,
        notes: formData.notes,
        entry_price: toFloatLoose(formData.entry_price),
        exit_price: toFloatLoose(formData.exit_price),
        position_size: toIntLoose(formData.position_size),
      };
      setTempEntries((prev) => [local, ...prev]);
      toast.info("Saved locally. Login or Continue as Guest to persist.");
      setFormData({
        ticker: "",
        notes: "",
        entry_price: "",
        exit_price: "",
        position_size: "",
      });
      return;
    }

    const payload = {
      ticker: formData.ticker,
      notes: formData.notes,
      entry_price: toFloatLoose(formData.entry_price),
      exit_price: toFloatLoose(formData.exit_price),
      position_size: toIntLoose(formData.position_size),
    };

    try {
      const created = await api("/api/journal", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      toast.success("Journal entry created successfully!");
      setFormData({
        ticker: "",
        notes: "",
        entry_price: "",
        exit_price: "",
        position_size: "",
      });

      setEntries((prev) => [normalizeJournal(created), ...prev]);
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(err.message || "Server error while creating journal entry.");
    }
  };

  const openEdit = (entry) => {
    if (!isAuthed && !isGuest) {
      toast.info("Login to edit entries.");
      return;
    }
    setEditData(entry);
    setIsEditing(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editData.id) return;

    setSavingEdit(true);
    try {
      await api(`/api/journal/${editData.id}`, {
        method: "PUT",
        body: JSON.stringify({
          notes: editData.notes,
          data: {
            ticker: editData.ticker,
            entry_price: toFloatLoose(editData.entry_price),
            exit_price: toFloatLoose(editData.exit_price),
            position_size: toIntLoose(editData.position_size),
          },
        }),
      });

      toast.success("Entry updated successfully!");
      setEntries((prev) =>
        prev.map((it) => (it.id === editData.id ? { ...it, ...editData } : it))
      );
      setIsEditing(false);
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!isAuthed && !isGuest) {
      toast.info("Login to delete entries.");
      return;
    }
    const original = [...entries];
    setEntries(entries.filter((e) => e.id !== entryId));
    try {
      await api(`/api/journal/${entryId}`, { method: "DELETE" });
      toast.success("Journal entry deleted successfully");
    } catch (err) {
      console.error("Delete error:", err);
      setEntries(original);
      toast.error(err.message || "Failed to delete entry");
    }
  };

  const rowsToShow = [...tempEntries, ...entries];

  return (
    <div className="min-h-screen text-white bg-[#121212] p-6">
      <GuestBanner page="journal" />

      <h2 className="text-2xl font-semibold mb-6">Trade Journaling</h2>

      <CardWrapper className="mb-6">
        <input
          type="text"
          placeholder="Filter by ticker"
          className="w-full p-2 rounded bg-[#1c1c1c] border border-[#2a2a2a] text-white placeholder-gray-400"
          onChange={(e) => setFilter(e.target.value.toUpperCase())}
        />
      </CardWrapper>

      <CardWrapper className="mb-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            name="ticker"
            value={formData.ticker}
            onChange={handleChange}
            placeholder="Ticker"
            className="w-full p-3 bg-[#1c1c1c] border border-[#2a2a2a] rounded text-white"
            required
          />
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Notes"
            className="w-full p-3 bg-[#1c1c1c] border border-[#2a2a2a] rounded text-white"
            required
          />
          <input
            type="text"
            name="entry_price"
            value={formData.entry_price}
            onChange={handleChange}
            placeholder="Entry Price ($100.50)"
            className="w-full p-3 bg-[#1c1c1c] border border-[#2a2a2a] rounded text-white"
            required
          />
          <input
            type="text"
            name="exit_price"
            value={formData.exit_price}
            onChange={handleChange}
            placeholder="Exit Price ($150)"
            className="w-full p-3 bg-[#1c1c1c] border border-[#2a2a2a] rounded text-white"
            required
          />
          <input
            type="text"
            name="position_size"
            value={formData.position_size}
            onChange={handleChange}
            placeholder="Position Size (e.g. 200 shares)"
            className="w-full p-3 bg-[#1c1c1c] border border-[#2a2a2a] rounded text-white"
            required
          />
          <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded w-full">
            Save Entry
          </button>
        </form>
      </CardWrapper>

      <CardWrapper>
        <h3 className="text-xl font-semibold mb-3">Previous Entries</h3>
        {loading ? (
          <p className="text-gray-300">Loading...</p>
        ) : rowsToShow.length === 0 ? (
          <p className="text-gray-400 italic">No entries yet.</p>
        ) : (
          <ul className="space-y-2">
            {rowsToShow
              .filter((entry) =>
                (entry.ticker || "").toUpperCase().includes(filter)
              )
              .map((entry) => (
                <li
                  key={entry.id}
                  className="border border-gray-800 p-4 rounded bg-[#151515] flex justify-between items-start"
                >
                  <div>
                    <p>
                      <strong>{entry.ticker}</strong>{" "}
                      {String(entry.id).startsWith("temp-") && (
                        <span className="text-xs text-gray-400">
                          (local only)
                        </span>
                      )}
                      - {entry.notes}
                    </p>
                    <p>
                      Entry: ${entry.entry_price ?? "-"} | Exit: $
                      {entry.exit_price ?? "-"} | Size:{" "}
                      {entry.position_size ?? "-"}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {String(entry.id).startsWith("temp-") ? (
                      <span className="text-xs text-gray-500">Local only</span>
                    ) : (
                      <>
                        <button
                          onClick={() => openEdit(entry)}
                          className="text-blue-400 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-400 text-sm"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
          </ul>
        )}
      </CardWrapper>

      <Modal
        isOpen={isEditing}
        onRequestClose={() => !savingEdit && setIsEditing(false)}
        className="bg-black p-6 rounded text-white max-w-lg mx-auto mt-20 border border-gray-700 w-[92%]"
        overlayClassName="fixed inset-0 bg-black/60 flex justify-center items-start z-[100]"
      >
        <h2 className="text-xl font-bold mb-4">Edit Journal Entry</h2>
        <form onSubmit={saveEdit} className="space-y-4">
          <input
            type="text"
            value={editData.ticker}
            onChange={(e) =>
              setEditData({ ...editData, ticker: e.target.value })
            }
            placeholder="Ticker"
            className="w-full p-3 bg-[#1c1c1c] border border-[#2a2a2a] rounded text-white"
            required
          />
          <textarea
            value={editData.notes}
            onChange={(e) =>
              setEditData({ ...editData, notes: e.target.value })
            }
            placeholder="Notes"
            className="w-full p-3 bg-[#1c1c1c] border border-[#2a2a2a] rounded text-white"
            required
          />
          <input
            type="text"
            value={editData.entry_price}
            onChange={(e) =>
              setEditData({ ...editData, entry_price: e.target.value })
            }
            placeholder="Entry Price"
            className="w-full p-3 bg-[#1c1c1c] border border-[#2a2a2a] rounded text-white"
            required
          />
          <input
            type="text"
            value={editData.exit_price}
            onChange={(e) =>
              setEditData({ ...editData, exit_price: e.target.value })
            }
            placeholder="Exit Price"
            className="w-full p-3 bg-[#1c1c1c] border border-[#2a2a2a] rounded text-white"
            required
          />
          <input
            type="text"
            value={editData.position_size}
            onChange={(e) =>
              setEditData({ ...editData, position_size: e.target.value })
            }
            placeholder="Position Size"
            className="w-full p-3 bg-[#1c1c1c] border border-[#2a2a2a] rounded text-white"
            required
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              disabled={savingEdit}
              className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={savingEdit}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded"
            >
              {savingEdit ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default TradeJournaling;
